import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DossierAreaState } from 'app/modules/dossier-area/dossier-area.module';
import { IAppState } from 'app/modules/shared-area/root-state';
import { DossierHelperService } from 'app/modules/dossier-area/services/dossier-helper/dossier-helper.service';
import { IDossierState } from './dossier.state';

// Base selectors
const dossierAreaState = createFeatureSelector<DossierAreaState>('dossier-area');
const sharedArea = (state: IAppState) => state.shared;
const showmMerkmaleSelector = createSelector(sharedArea, state => state.panels.dossierView.shownMerkmale);
const showAllMerkmaleSelector = createSelector(sharedArea, state => state.panels.dossierView.showAllMerkmale);

export const getDossierState = createSelector(
  dossierAreaState,
  (state: DossierAreaState) => state.dossier
);

// Basic state selectors
export const getSubstanceId = createSelector(
  getDossierState,
  state => state.id
);

export const getDossier = createSelector(
  getDossierState,
  state => state.dossier
);

export const getFilteredDossier = createSelector(
  getDossier,
  showmMerkmaleSelector,
  showAllMerkmaleSelector,
  (dossier, shownMerkmale, showAllMerkmale) =>
    dossier ? DossierHelperService.removeMerkmaleFromDossier(dossier, shownMerkmale, showAllMerkmale) : null,
);

export const isDossierLoading = createSelector(
  getDossierState,
  state => state.isLoading
);

export const isDossierDeleted = createSelector(
  getDossierState,
  state => state.isDeleted === true && state.isDeleting === false,
);

export const getRoleContext = createSelector(
  getDossierState,
  state => state.roleContext
);

export const getDossierType = createSelector(
  getDossier,
  state => (state ? state.type : null)
);

// Render state selectors
export const getRenderingItems = createSelector(
  getDossierState,
  state => state.renderingItems
);

export const getIsPartialUpdateInProgress = createSelector(
  getDossierState,
  state => state.isPartialUpdateInProgress
);

export const isPanelRenderingInProgress = createSelector(
  getRenderingItems,
  getIsPartialUpdateInProgress,
  (renderingItems, isPartialUpdateInProgress) => renderingItems > 0 || isPartialUpdateInProgress,
);

export const getRenderState = createSelector(
  getDossierState,
  state => state.renderState
);

export const getPanelRenderState = (panelName: string) =>
  createSelector(getRenderState, state => state.get(panelName));

export const isPanelRendering = (panelName: string) =>
  createSelector(getPanelRenderState(panelName), state => {
    return state === 'rendering';
  });

export const isPanelRendered = (panelName: string) =>
  createSelector(getPanelRenderState(panelName), state => {
    return state === 'rendered';
  });

// Search state selectors
export const getSearchState = createSelector(
  getDossierState,
  state => state.searchState
);

export const getSearch = createSelector(
  getSearchState,
  state => state.search
);

export const getIsIgnoreCase = createSelector(
  getSearchState,
  state => state.isIgnoreCase
);

export const getSearchHits = createSelector(
  getSearchState,
  state => state.hits
);

export const getNumberOfSearchResults = createSelector(
  getSearchState,
  state => state.hits.size
);

export const getCurrentSearchHit = createSelector(
  getSearchState,
  state => state.currentHit
);

export const getSearchStateState = createSelector(
  getSearchState,
  state => state.state
);

// Submit state selectors
export const getIsSubmitBusy = createSelector(
  getDossierState,
  state => state.submitIsBusy
);

// Copy Sachverhalte selectors
const getCopySachverhalteState = createSelector(
  getDossierState,
  state => state.copySachverhalte
);

export const getCopySachverhalteIsLoading = createSelector(
  getCopySachverhalteState,
  state => state.isLoading
);

// Thesaurus mode selector
export const getThesaurusMode = createSelector(
  getDossierState,
  state => state.thesaurusMode
);

// Sachverhalt toggle selectors
export const getSachverhaltToggle = createSelector(
  getDossierState,
  state => state.sachverhaltToggle
);

export const isSachverhaltToggleVisible = createSelector(
  getSachverhaltToggle,
  toggle => toggle.isVisible
);

export const getSachverhaltToggleMode = createSelector(
  getSachverhaltToggle,
  toggle => toggle.mode
);

export const getSelectedSachverhalte = createSelector(
  getSachverhaltToggle,
  toggle => toggle.selectedSachverhalte
);

export const isSachverhaltSelected = (sachverhaltId: number, erfassungSachverhaltId: number) =>
  createSelector(
    getSelectedSachverhalte,
    sachverhalte => sachverhalte.some(
      s => s.sachverhaltId === sachverhaltId && s.erfassungSachverhaltId === erfassungSachverhaltId
    )
  );

export const isSachverhaltToggleDisabled = (sachverhaltId: number, erfassungSachverhaltId: number, merkmalId: number) =>
  createSelector(
    getSachverhaltToggle,
    toggle => {
      // If toggle is not visible, don't disable (no logic applies)
      if (!toggle.isVisible) {
        return false;
      }
      
      // If mode is 'copy', disable if BOTH sachverhaltId AND erfassungSachverhaltId are NOT set
      if (toggle.mode === 'copy') {
        return sachverhaltId == null && erfassungSachverhaltId == null;
      }
      
      // If mode is 'feedback', disable if sachverhaltId is null
      if (toggle.mode === 'feedback') {
        return sachverhaltId == null;
      }
      
      return false;
    }
  );

// Legacy selector aliases for backward compatibility
export const dossierSelector = getDossierState;
export const renderingItemsSelector = getRenderingItems;
export const isPartialUpdateInProgressSelector = getIsPartialUpdateInProgress;
export const isPanelRenderingInProgressSelector = isPanelRenderingInProgress;
export const renderStateSelector = getRenderState;
export const panelRenderStateSelector = getPanelRenderState;
export const panelIsRenderingSelector = isPanelRendering;
export const panelIsRenderedSelector = isPanelRendered;
export const searchStateSelector = getSearchState;
export const searchSelector = getSearch;
export const ignoreCaseSelector = getIsIgnoreCase;
export const searchHitsSelector = getSearchHits;
export const getNumberOfSearchResultsSelector = getNumberOfSearchResults;
export const getCurrentSearchHitSelector = getCurrentSearchHit;
export const isSubmitBusy = getIsSubmitBusy;
export const searchStateStateSelector = getSearchStateState;
export const copySachverhalteIdsSelector = getSelectedSachverhalte;
export const copySachverhalteIsLoadingSelector = getCopySachverhalteIsLoading;

// Assignable Users Selectors
export const getAssignableUsersState = createSelector(
  getDossierState,
  state => state.assignableUsers
);

export const getAssignableUsers = createSelector(
  getAssignableUsersState,
  state => state.users
);

export const getAssignableUsersLoading = createSelector(
  getAssignableUsersState,
  state => state.isLoading
);

export const getAssignableUsersError = createSelector(
  getAssignableUsersState,
  state => state.error
);
